import { useCallback, useEffect, useRef, useState } from 'react'

import { api } from '@carrent/api'

/**
 * Поток событий.
 *
 * Подключается к SSE, переподключается с нарастающей паузой,
 * доигрывает пропущенное по Last-Event-ID и после нескольких
 * неудач мягко уходит в опрос — реалтайм полезен, но приложение
 * без него работать обязано.
 */

const MAX_RECONNECT_ATTEMPTS = 5
/**
 * Сколько идентификаторов помним для отсева повторов.
 *
 * Повтор возможен: при переподключении сервер доигрывает события
 * от последнего известного идентификатора, и граница может наложиться.
 * Обработать событие дважды — значит дважды перезагрузить список
 * или показать сообщение дублем.
 */
const SEEN_LIMIT = 200
const BASE_RECONNECT_MS = 1_000
const MAX_RECONNECT_MS = 30_000

export const STATUS_CONNECTING = 'connecting'
export const STATUS_LIVE = 'live'
export const STATUS_POLLING = 'polling'
export const STATUS_OFF = 'off'

function reconnectDelay(attempt) {
  const delay = BASE_RECONNECT_MS * 2 ** Math.max(0, attempt - 1)
  // Джиттер: иначе все вкладки, потерявшие связь одновременно,
  // одновременно же и постучатся.
  return Math.min(delay * (0.85 + Math.random() * 0.3), MAX_RECONNECT_MS)
}

/**
 * @param {(event: {type: string, payload: object}) => void} onEvent
 * @param {{ enabled?: boolean, onFallback?: () => void }} options
 */
export function useEventStream(onEvent, { enabled = true, onFallback } = {}) {
  const [status, setStatus] = useState(STATUS_CONNECTING)
  const sourceRef = useRef(null)
  const attemptsRef = useRef(0)
  const timerRef = useRef(null)
  const lastIdRef = useRef(null)
  const seenRef = useRef(new Set())
  const handlerRef = useRef(onEvent)
  const stoppedRef = useRef(false)

  // Обработчик меняется на каждом рендере — храним в ref,
  // чтобы не пересоздавать соединение при каждом обновлении.
  handlerRef.current = onEvent

  const close = useCallback(() => {
    clearTimeout(timerRef.current)
    sourceRef.current?.close()
    sourceRef.current = null
  }, [])

  const connect = useCallback(async () => {
    if (stoppedRef.current) return

    try {
      // EventSource не умеет заголовки, поэтому подключение
      // авторизуется отдельным короткоживущим токеном: обычный
      // access-токен в URL попал бы в логи и историю браузера.
      const { data } = await api.post('/events/token')
      const token = data.data?.token
      if (!token) throw new Error('Токен не выдан')

      const params = new URLSearchParams({ token })
      if (lastIdRef.current) params.set('last_event_id', lastIdRef.current)

      const source = new EventSource(`/api/events/stream?${params}`)
      sourceRef.current = source

      source.onopen = () => {
        attemptsRef.current = 0
        setStatus(STATUS_LIVE)
      }

      source.onmessage = (event) => {
        const id = event.lastEventId

        // Повтор пропускаем, но идентификатор всё равно запоминаем:
        // иначе при следующем разрыве доигрывание начнётся раньше,
        // чем нужно.
        if (id) {
          if (seenRef.current.has(id)) {
            lastIdRef.current = id
            return
          }
          seenRef.current.add(id)
          if (seenRef.current.size > SEEN_LIMIT) {
            // Set сохраняет порядок вставки — убираем самый старый.
            seenRef.current.delete(seenRef.current.values().next().value)
          }
          lastIdRef.current = id
        }

        try {
          handlerRef.current?.(JSON.parse(event.data))
        } catch {
          // Битое событие не должно рвать соединение.
        }
      }

      source.onerror = () => {
        source.close()
        sourceRef.current = null
        scheduleReconnect()
      }
    } catch {
      scheduleReconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const scheduleReconnect = useCallback(() => {
    if (stoppedRef.current) return

    attemptsRef.current += 1

    if (attemptsRef.current > MAX_RECONNECT_ATTEMPTS) {
      // Не сдаёмся молча: переходим на опрос и сообщаем об этом.
      // Данные важнее свежести, а бесконечные попытки съедают батарею.
      setStatus(STATUS_POLLING)
      onFallback?.()
      return
    }

    setStatus(STATUS_CONNECTING)
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(connect, reconnectDelay(attemptsRef.current))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connect])

  useEffect(() => {
    if (!enabled || typeof EventSource === 'undefined') {
      setStatus(STATUS_OFF)
      return undefined
    }

    stoppedRef.current = false
    connect()

    return () => {
      stoppedRef.current = true
      close()
    }
  }, [enabled, connect, close])

  // Возврат на вкладку — повод проверить связь: браузер мог
  // усыпить соединение в фоне, не сообщив об ошибке.
  useEffect(() => {
    if (!enabled) return undefined

    const onVisible = () => {
      if (globalThis.document?.visibilityState !== 'visible') return
      if (sourceRef.current?.readyState === EventSource.OPEN) return
      attemptsRef.current = 0
      connect()
    }

    globalThis.document?.addEventListener('visibilitychange', onVisible)
    return () => globalThis.document?.removeEventListener('visibilitychange', onVisible)
  }, [enabled, connect])

  return {
    status,
    isLive: status === STATUS_LIVE,
    /** Принудительно переподключиться. */
    reconnect: useCallback(() => {
      close()
      attemptsRef.current = 0
      connect()
    }, [close, connect]),
  }
}
